import React, { useEffect } from 'react';
import LoggedInLayout from '@merchant-portal/layouts/logged-in-layout';
import { HeaderMerchant, styled } from '@payright/web-components';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@merchant-portal/slices';
import { SCCustomerDetails } from './expense-household-details-css';
import { submitExpenseHouseholdDetails } from '@merchant-portal/slices/customer';
import { AppDispatch } from '@merchant-portal/util/store';
import {
  FormHouseholdExpenses,
  FormHouseholdExpensesFormData,
} from './household-expenses/components/form-household-expenses';
import { useQuery } from '@merchant-portal/util/hooks';
import { fetchCheckoutAttributes } from '@merchant-portal/slices/checkout';
import { getPlanDetails } from '@merchant-portal/slices/plans';
import Loader from '@merchant-portal/components/loader';

const SCLoader = styled(Loader)`
  padding-bottom: 3rem;
`;

export const ExpenseHouseholdDetails = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();

  const queryParamCheckoutId = query.get('checkoutId');

  const {
    loading: loadingCheckout,
    checkout,
    errorMessage: checkoutErrorMessage,
  } = useSelector((state: RootState) => state.checkout);

  const { planDetails: plan, loading: loadingPlan } = useSelector(
    (state: RootState) => state.plans
  );

  const {
    errorMessage: customerErrorMessage,
    loading: loadingCustomer,
    householdDetails,
  } = useSelector((state: RootState) => state.customer);

  const loading = loadingCheckout || loadingCustomer || loadingPlan || false;
  const errorMessage = checkoutErrorMessage || customerErrorMessage;

  const checkoutId = checkout?.id ?? '';
  const planId = checkout?.planId ?? '';
  const assessmentTier = plan?.assessmentTier ?? '';
  const showLivingExpenses = assessmentTier === '2' || assessmentTier === '3';
  const showFinancialExpenses = assessmentTier === '3';

  useEffect(() => {
    if (queryParamCheckoutId !== null && queryParamCheckoutId !== checkoutId) {
      dispatch(fetchCheckoutAttributes(queryParamCheckoutId));
    }
  }, [checkoutId, dispatch, queryParamCheckoutId]);

  useEffect(() => {
    if (planId) {
      dispatch(getPlanDetails(planId));
    }
  }, [dispatch, planId]);

  const formSubmitHandlerExpenseHouseholdDetails = async (
    formData: FormHouseholdExpensesFormData
  ) => {
    const checkoutId = checkout!.id!;

    const householdDetails = {
      dependants: formData.householdDetails.dependants,
      relationshipStatus: formData.householdDetails.relationshipStatus,
      incomePercentage:
        formData.householdDetails.relationshipStatus === 'Single'
          ? 100
          : Number(formData.householdDetails.incomePercentage ?? 0),
    };

    const nextStep = await dispatch(
      submitExpenseHouseholdDetails(checkoutId, {
        householdDetails,
        ...(showLivingExpenses
          ? {
              expenseDetails: {
                ...(showFinancialExpenses
                  ? {
                      financialExpensesAmount: Number(
                        formData.expenseDetails.financialExpensesAmount ?? 0
                      ),
                      financialExpensesFrequency:
                        formData.expenseDetails.financialExpensesFrequency,
                    }
                  : {}),
                livingExpensesAmount: Number(formData.expenseDetails.livingExpensesAmount ?? 0),
                livingExpensesFrequency: formData.expenseDetails.livingExpensesFrequency,
                loanCount: Number(formData.expenseDetails.loanCount),
              },
            }
          : {}),
      })
    );

    if (nextStep === 'Bank Statements') {
      history.push(`/plans/new/plan-result/${checkout?.planId}`);
    } else {
      history.push(`/plans/new/summary?checkoutId=${checkoutId}`);
    }
  };

  return (
    <SCCustomerDetails>
      <LoggedInLayout contentMarginTop={0} activePage="plans/new">
        <HeaderMerchant title="New Plan" activeStep={1} setActiveStep={(_step: number) => {}} />
        {loading && <SCLoader text="Loading household and expenses information..." />}
        {!loading && (
          <FormHouseholdExpenses
            showFinancialExpenses={showFinancialExpenses}
            showLivingExpenses={showLivingExpenses}
            householdDetails={householdDetails}
            errorMessage={errorMessage}
            loading={loading}
            onSubmit={formSubmitHandlerExpenseHouseholdDetails}
          />
        )}
      </LoggedInLayout>
    </SCCustomerDetails>
  );
};
